import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import Footer from "@/components/Footer";

const SLAB_PACKAGES = [
  ["Slab 0", "15 Days", "0%", "At least 7 days before completion"],
  ["Slab 1", "30 Days", "10%", "At least 7 days before completion"],
  ["Slab 2", "45 Days", "20%", "At least 15 days before completion"],
  ["Slab 3", "60 Days", "30%", "At least 15 days before completion"],
  ["Slab 4", "90 Days", "40%", "At least 30 days before completion"],
  ["Slab 5", "6 Months", "As agreed / applicable rate", "At least 45 days before completion"],
];

const REFUND_TABLE = [
  ["Slab 0", "₹1,000", "0%", "₹1,000"],
  ["Slab 1", "₹1,000", "10%", "₹900"],
  ["Slab 2", "₹1,000", "20%", "₹800"],
  ["Slab 3", "₹1,000", "30%", "₹700"],
  ["Slab 4", "₹1,000", "40%", "₹600"],
  ["Slab 5", "As agreed", "Special rule", "See Section 19"],
];

const SECTIONS = [
  {
    n: "1",
    title: "Introduction",
    body: [
      "These Terms of Use & Service Agreement (“Terms”) constitute a legally binding agreement between Sharpin Healthcare Services (“Sharpin”, “Company”, “we”, “us” or “our”) and the person or entity purchasing, booking, requesting, accessing or receiving services from Sharpin (“Customer”, “you” or “your”).",
      "These Terms govern the provision of healthcare staffing, homecare support, nursing, caregiver, patient-attendant, GDA and other related services offered by Sharpin, whether arranged through: the Sharpin website; telephone; WhatsApp; email; physical/offline communication; service agreements; quotations; invoices; payment links; representatives or authorised personnel; or any other approved communication channel.",
      "By making payment, confirming a service, accepting a quotation, signing a service agreement, commencing services, or otherwise instructing Sharpin to provide services, the Customer acknowledges that the Customer has read, understood and accepted these Terms.",
    ],
  },
  {
    n: "2",
    title: "Definitions",
    body: [
      "2.1 “Services” — Means healthcare manpower, homecare, nursing, caregiver, GDA, patient-attendant, companion-care, support services and other services specifically agreed between Sharpin and the Customer.",
      "2.2 “Caregiver/Staff” — Means a nurse, GDA, caregiver, patient attendant, healthcare worker, domestic-support worker or other personnel deployed or arranged by Sharpin, depending upon the service purchased.",
      "2.3 “Package” — Means the service duration and pricing arrangement selected by the Customer, including Slab 0 through Slab 5.",
      "2.4 “Service Commencement Date” — Means the date on which the agreed service is scheduled to commence.",
      "2.5 “Consumed Services” — Means services actually provided, scheduled, allocated, mobilised, or otherwise committed for the Customer, as determined under the applicable service arrangement.",
      "2.6 “Remaining Services” — Means the portion of the contracted service period that has not been consumed, subject to the terms of this Agreement.",
      "2.7 “Force Majeure Event” — Means an event beyond the reasonable control of Sharpin, including natural disasters, flood, earthquake, fire, epidemic, pandemic, government restrictions, governmental orders, war, civil disturbance, terrorism, strikes, transportation disruption, infrastructure failure, utility failure, widespread communication failure, or any similar event materially affecting the Company's ability to provide services.",
    ],
  },
  {
    n: "3",
    title: "Nature and Scope of Services",
    body: [
      "3.1 Sharpin operates as a healthcare and manpower service provider and may arrange or deploy suitable personnel according to the Customer's stated requirements.",
      "3.2 The exact nature of services shall be determined by the quotation, service agreement, package selected, invoice, written confirmation or other communication accepted by both parties.",
      "3.3 Services may include, depending upon the package: Nursing support; GDA services; Caregiver services; Patient-attendant services; Elder-care support; Post-hospitalisation support; Companionship; Assistance with activities of daily living; Basic monitoring as specifically agreed; Other healthcare support services agreed in writing.",
      "3.4 Sharpin shall use reasonable efforts to provide personnel reasonably suitable for the requirements communicated by the Customer.",
      "3.5 Sharpin does not guarantee that every individual deployed will possess qualifications beyond those expressly confirmed in writing.",
    ],
  },
  {
    n: "4",
    title: "No Unauthorised Medical Practice",
    body: [
      "4.1 Unless expressly provided by a suitably qualified and authorised professional, caregivers, GDAs and attendants shall not independently diagnose medical conditions, prescribe medicines, alter medical treatment, or undertake procedures outside their qualifications and authorised scope.",
      "4.2 The Customer shall remain responsible for ensuring that medical treatment, prescriptions, diagnoses and clinical decisions are made by appropriately qualified healthcare professionals.",
      "4.3 Sharpin shall not be responsible for medical decisions made by the Customer, patient's family members or independent healthcare professionals.",
    ],
  },
  {
    n: "5",
    title: "Customer Information and Requirements",
    body: [
      "5.1 The Customer shall provide accurate and complete information reasonably necessary for service deployment.",
      "5.2 Such information may include: Patient requirements; Service location; Required duration; Required shift; Gender preference, where applicable; Qualification requirements; Mobility or assistance requirements; Relevant safety information; Emergency contact details; Any other material information required for safe service delivery.",
      "5.3 Failure to disclose material information may affect Sharpin's ability to allocate suitable personnel.",
      "5.4 If the actual requirement materially differs from the requirement originally communicated, Sharpin may revise the service arrangement and/or charges with Customer approval.",
    ],
  },
  {
    n: "6",
    title: "Advance Payment Policy",
    body: [
      "6.1 All services are payable in advance, unless Sharpin expressly agrees otherwise in writing.",
      "6.2 Payment shall be considered confirmed only when the applicable amount has been successfully received by Sharpin.",
      "6.3 Sharpin may incur costs before, during or immediately after receiving payment, including: Recruitment; Candidate sourcing; Verification; Staff allocation; Scheduling; Mobilisation; Administrative processing; Replacement planning; Operational coordination; Opportunity costs; and Resource reservation.",
      "6.4 Accordingly, payment for a package constitutes a commercial commitment by the Customer to the selected service duration, subject to the cancellation and refund provisions contained in these Terms.",
    ],
  },
  {
    n: "7",
    title: "Slab-Wise Package Structure",
    body: [
      "Sharpin may offer services under the following package structure:",
      "TABLE:SLAB_PACKAGES",
      "The final price, discount and applicable package shall be those expressly stated in the quotation, invoice or service agreement issued to the Customer.",
    ],
  },
  {
    n: "8",
    title: "Slab 0 – 15-Day Package",
    body: [
      "8.1 Slab 0 represents the standard 15-day service package.",
      "8.2 No slab discount is ordinarily applicable to Slab 0 unless otherwise agreed.",
      "8.3 If the Customer wishes to continue services beyond the contracted period, payment for continuation should ordinarily be made at least 7 days before completion of the existing service period.",
    ],
  },
  {
    n: "9",
    title: "Slab 1 – 30-Day Package",
    body: [
      "9.1 Slab 1 represents a 30-day service package.",
      "9.2 Where the applicable commercial offer provides a 10% discount, the discounted rate shall apply subject to the Customer completing the agreed package duration.",
      "9.3 Continuation/renewal payment should ordinarily be made at least 7 days before completion of the existing service period.",
    ],
  },
  {
    n: "10",
    title: "Slab 2 – 45-Day Package",
    body: [
      "10.1 Slab 2 represents a 45-day service package.",
      "10.2 Where applicable, the package may carry a 20% discount against the relevant base rate.",
      "10.3 Continuation/renewal payment should ordinarily be made at least 15 days before completion of the existing service period.",
    ],
  },
  {
    n: "11",
    title: "Slab 3 – 60-Day Package",
    body: [
      "11.1 Slab 3 represents a 60-day service package.",
      "11.2 Where applicable, the package may carry a 30% discount against the relevant base rate.",
      "11.3 Continuation/renewal payment should ordinarily be made at least 15 days before completion of the existing service period.",
      "11.4 Any additional extension or grace period shall be subject to written confirmation from Sharpin and shall not be deemed automatic.",
    ],
  },
  {
    n: "12",
    title: "Slab 4 – 90-Day Package",
    body: [
      "12.1 Slab 4 represents a 90-day service package.",
      "12.2 Where applicable, the package may carry a 40% discount against the relevant base rate.",
      "12.3 Continuation/renewal payment should ordinarily be made at least 30 days before completion of the existing service period.",
      "12.4 Any extension or grace period shall be subject to availability and written confirmation.",
    ],
  },
  {
    n: "13",
    title: "Slab 5 – Six-Month Package",
    body: [
      "13.1 Slab 5 represents a long-term service commitment of approximately six months, subject to the specific service agreement.",
      "13.2 The applicable commercial rate shall be the rate expressly agreed with the Customer.",
      "13.3 The Customer should ordinarily make continuation/renewal payment at least 45 days before completion of the existing package.",
      "13.4 Any grace period, extension or continuation arrangement shall be valid only where expressly confirmed by Sharpin in writing.",
      "13.5 Because Slab 5 involves a long-term commitment and potentially substantial advance operational allocation, it is subject to the special cancellation provisions contained below.",
    ],
  },
  {
    n: "14",
    title: "Zero-Cancellation Policy",
    body: [
      "14.1 Sharpin operates a Zero-Cancellation Policy for confirmed prepaid service packages, subject to the express exceptions and statutory rights set out in these Terms.",
      "14.2 Once a Customer has made payment and Sharpin has confirmed the service/package, the Customer shall not ordinarily be entitled to cancel the package merely because of: Change of mind; Change of personal circumstances; Change of requirement; Relocation; Change in family circumstances; Financial inconvenience arising after booking; Decision to engage another service provider; Temporary non-use of the service; Dissatisfaction unrelated to a material service failure; or Any other discretionary reason.",
      "14.3 In such circumstances, Sharpin shall have no automatic obligation to provide cancellation or refund.",
      "14.4 The Zero-Cancellation Policy exists because Sharpin may commit resources immediately after receiving payment, including recruitment, manpower allocation, scheduling, onboarding, mobilisation, administrative processing, replacement planning and opportunity costs.",
      "14.5 Nothing in this clause shall be interpreted to exclude or restrict any right that cannot lawfully be excluded under applicable Indian law.",
    ],
  },
  {
    n: "15",
    title: "Cancellation Notice",
    body: [
      "15.1 Where the Customer intends to discontinue services, the Customer shall provide written notice at least 48 hours in advance.",
      "15.2 Cancellation/discontinuation requests must be sent to: Email: info@sharpinhealthcareservices.com",
      "15.3 WhatsApp or telephone communication may be used for operational coordination; however, the Customer should also provide written confirmation through the designated email address where cancellation or refund is being requested.",
      "15.4 Providing 48 hours' notice is an operational requirement and shall not, by itself, create an unconditional entitlement to a refund.",
    ],
  },
  {
    n: "16",
    title: "Exceptional Refund Circumstances",
    body: [
      "A refund or financial adjustment may be considered where one or more of the following circumstances apply:",
      "a. Force Majeure Event;",
      "b. A bona fide legal dispute concerning the service arrangement;",
      "c. A written settlement mutually accepted by both parties;",
      "d. An order or direction of a competent court, Consumer Commission, regulator or other competent authority;",
      "e. A circumstance in which applicable law requires a refund or financial adjustment; or",
      "f. Any other exceptional circumstance expressly approved in writing by Sharpin.",
      "The applicable refund shall be determined in accordance with the relevant slab, services consumed, remaining services, costs incurred and applicable law.",
    ],
  },
  {
    n: "17",
    title: "Slab-Wise Refund Principle",
    body: [
      "Where a refund becomes applicable under these Terms, the refund shall not necessarily be calculated solely by dividing the total package amount by the number of days.",
      "Where a Customer purchased a longer-duration package at a discounted rate and subsequently qualifies for a refund, Sharpin may recalculate the value of services actually consumed using the applicable slab/rate for the period consumed.",
      "This is intended to prevent a Customer from obtaining the benefit of a long-duration discount while terminating the package before completing the committed duration.",
    ],
  },
  {
    n: "18",
    title: "Slab-Wise Refund Table",
    body: [
      "For applicable refunds, the following commercial structure may be used:",
      "TABLE:REFUND_TABLE",
      "The above ₹1,000 figures are an illustrative slab reference and shall not override the actual price stated in the Customer's quotation, invoice or service agreement.",
    ],
  },
  {
    n: "19",
    title: "Slab 5 – 50% Pro-Rata Refund for Exceptional Circumstances",
    body: [
      "19.1 Slab 5 is subject to a Zero-Cancellation Policy, except where a refund is triggered under the exceptional circumstances expressly recognised under these Terms or applicable law.",
      "19.2 Where a refund is determined to be applicable because of a Force Majeure Event, bona fide legal dispute, competent authority order, mutual written settlement or mandatory applicable law, the maximum commercial refund applicable to the remaining/unconsumed service period shall ordinarily be: 50% of the pro-rata value of the remaining/unconsumed services.",
      "19.3 The 50% calculation shall apply only to the remaining/unconsumed services and shall not constitute a refund of 50% of the total amount originally paid.",
      "19.4 The calculation may be expressed as: Pro-Rata Value of Remaining Services × 50% = Maximum Refundable Amount",
      "19.5 Before determining the final refund, Sharpin may deduct or adjust, where legally permissible: Value of services already consumed; Staff mobilisation costs; Recruitment and onboarding costs; Administrative costs; Replacement costs; Operational costs; Applicable taxes or statutory charges; Outstanding amounts; Other reasonably incurred non-recoverable costs; and Other amounts permitted under the service agreement or applicable law.",
      "19.6 The final refund, if any, shall therefore be determined after considering the actual circumstances and applicable legal requirements.",
    ],
  },
  {
    n: "20",
    title: "No Automatic Refund for Force Majeure",
    body: [
      "20.1 The occurrence of a Force Majeure Event shall not automatically create an unconditional right to a full refund.",
      "20.2 Sharpin shall assess: Services already provided; Services remaining; Costs already incurred; Staff commitments; Operational feasibility; Possibility of alternative service arrangements; Applicable slab; Circumstances causing the Force Majeure Event; and Applicable law.",
      "20.3 Where appropriate and operationally feasible, Sharpin may attempt to reschedule, suspend, modify or provide an alternative service arrangement.",
      "20.4 Where a refund is applicable, the amount shall be determined under these Terms and applicable law.",
    ],
  },
  {
    n: "21",
    title: "Legal Dispute",
    body: [
      "21.1 If a genuine dispute arises between Sharpin and the Customer concerning the service arrangement, the parties shall first attempt to resolve the matter through good-faith communication.",
      "21.2 The Customer shall provide reasonable details and supporting documentation concerning the dispute.",
      "21.3 Where the dispute is formally referred to a court, Consumer Commission, regulator, arbitrator or other competent authority, any refund or financial adjustment shall remain subject to the applicable agreement, settlement or order.",
      "21.4 Nothing in these Terms shall prevent either party from exercising a legal remedy that cannot lawfully be waived.",
    ],
  },
  {
    n: "22",
    title: "Staff Deployment and Replacement",
    body: [
      "22.1 Sharpin may replace deployed personnel where reasonably necessary due to: Leave; Illness; Personal emergency; Unavailability; Performance concerns; Safety concerns; Misconduct; Resignation; Operational requirements; or Any other legitimate reason.",
      "22.2 Sharpin shall use reasonable efforts to arrange a suitable replacement where replacement is included within the applicable service arrangement.",
      "22.3 Replacement availability may depend upon location, shift, qualification, gender requirement and urgency.",
      "22.4 A replacement request shall not automatically constitute a cancellation of the package.",
    ],
  },
  {
    n: "23",
    title: "Staff Leave and Absence",
    body: [
      "23.1 Personnel may require leave, rest periods or absence due to illness, emergency or other legitimate circumstances.",
      "23.2 Sharpin shall make reasonable efforts to manage planned and unexpected absences in accordance with the applicable service arrangement.",
      "23.3 Where a replacement is requested, the availability of replacement personnel may depend upon local manpower availability.",
    ],
  },
  {
    n: "24",
    title: "Customer Responsibilities",
    body: [
      "The Customer shall:",
      "a. Provide a safe working and service environment;",
      "b. Treat deployed personnel with dignity and respect;",
      "c. Provide reasonable access to the premises required to perform the agreed services;",
      "d. Provide accurate patient/service information;",
      "e. Not require personnel to perform unlawful activities;",
      "f. Not require personnel to undertake tasks outside their agreed role or qualifications;",
      "g. Make payments on time;",
      "h. Not directly employ or engage Sharpin personnel outside the agreed arrangement where such conduct violates the applicable service agreement;",
      "i. Not make unauthorised deductions from Sharpin's charges;",
      "j. Immediately report serious incidents or misconduct to Sharpin; and",
      "k. Cooperate reasonably with Sharpin's operations team.",
    ],
  },
  {
    n: "25",
    title: "Direct Payment to Deployed Staff",
    body: [
      "25.1 Unless expressly authorised by Sharpin in writing, the Customer should not make service payments, advances or other commercial payments directly to deployed personnel.",
      "25.2 Any unauthorised direct payment may not be treated as payment against the Customer's outstanding liability to Sharpin.",
      "25.3 Customers should report any demand for unauthorised payment to Sharpin immediately.",
    ],
  },
  {
    n: "26",
    title: "Service Location",
    body: [
      "26.1 Services shall be provided at the location agreed at the time of booking.",
      "26.2 If the Customer requests relocation of services, Sharpin may review: Availability; Distance; Travel requirements; Staff willingness; Safety; Additional costs; and Operational feasibility.",
      "26.3 Sharpin may require additional charges for a materially different service location.",
    ],
  },
  {
    n: "27",
    title: "Working Conditions and Safety",
    body: [
      "27.1 The Customer shall maintain reasonable safety conditions at the service location.",
      "27.2 Sharpin may suspend or withdraw personnel where continuing deployment presents a material safety risk, unlawful working condition, violence, harassment, abuse, threat or other serious concern.",
      "27.3 In such circumstances, Sharpin shall communicate with the Customer regarding the next operational steps.",
    ],
  },
  {
    n: "28",
    title: "Medical Emergencies",
    body: [
      "28.1 Sharpin's homecare support personnel are not a substitute for emergency medical services unless expressly contracted and appropriately qualified for such services.",
      "28.2 In a medical emergency, the Customer/patient's family should immediately contact the appropriate emergency medical service or treating healthcare professional.",
      "28.3 Sharpin personnel may assist within their training and authorised scope but shall not be expected to make independent emergency medical decisions beyond their competence.",
    ],
  },
  {
    n: "29",
    title: "Service Limitations",
    body: [
      "Unless expressly agreed in writing, Sharpin's general caregiver/GDA services do not automatically include: Independent medical diagnosis; Prescription of medicines; Surgical procedures; Independent clinical decision-making; Specialist nursing procedures outside the worker's qualification; Household work unrelated to the patient's care; Financial transactions on behalf of the Customer; Unauthorised handling of valuables; Driving vehicles; Any unlawful activity; or Any task outside the agreed job description.",
    ],
  },
  {
    n: "30",
    title: "Confidentiality",
    body: [
      "30.1 Sharpin and its personnel shall use reasonable efforts to maintain confidentiality concerning information received in connection with service delivery.",
      "30.2 The Customer shall also maintain confidentiality concerning Sharpin's commercial information, pricing, internal processes and personnel information.",
      "30.3 Confidentiality obligations shall not apply where disclosure is required by law, court order, regulatory requirement, emergency necessity or other lawful authority.",
    ],
  },
  {
    n: "31",
    title: "Personal and Patient Information",
    body: [
      "31.1 The Customer may be required to provide personal information and, where relevant, patient-related information to enable service delivery.",
      "31.2 Such information shall be handled in accordance with Sharpin's Privacy Policy.",
      "31.3 The Customer represents that, where the Customer provides information relating to another individual, the Customer is authorised to provide such information or has a lawful basis to do so.",
    ],
  },
  {
    n: "32",
    title: "Payment Default",
    body: [
      "32.1 If a payment becomes overdue, Sharpin may, subject to the applicable agreement and law: Suspend services; Withhold further deployment; Require payment before continuation; Recover outstanding dues; and/or Take other lawful action.",
      "32.2 Continued service after a payment default shall not constitute a waiver of Sharpin's right to recover outstanding amounts.",
    ],
  },
  {
    n: "33",
    title: "Taxes and Government Charges",
    body: [
      "33.1 Applicable GST and other statutory charges shall be payable in accordance with applicable law and the invoice issued by Sharpin.",
      "33.2 Any statutory change affecting the cost of services may be reflected in future invoices or adjustments as legally permissible.",
    ],
  },
  {
    n: "34",
    title: "Refund Process",
    body: [
      "34.1 Where a refund is determined to be applicable, the Customer may be required to provide: Booking/invoice details; Payment details; Cancellation request; Reason for the request; Supporting documents, where relevant; and Bank details required for processing.",
      "34.2 Refunds shall ordinarily be processed through the payment/banking method reasonably determined by Sharpin.",
      "34.3 Processing time may depend upon banking/payment-provider procedures and verification requirements.",
      "34.4 Sharpin shall not be responsible for delays caused solely by banks, payment gateways or other third-party financial institutions.",
    ],
  },
  {
    n: "35",
    title: "No Waiver",
    body: [
      "Failure by Sharpin to immediately enforce any provision of these Terms shall not constitute a waiver of that provision or of Sharpin's future right to enforce it.",
    ],
  },
  {
    n: "36",
    title: "Limitation of Liability",
    body: [
      "36.1 To the extent permitted by applicable law, Sharpin shall not be liable for indirect, incidental, consequential or speculative losses arising from service arrangements.",
      "36.2 Sharpin's liability shall not extend to matters outside its reasonable control.",
      "36.3 Nothing in these Terms shall exclude liability that cannot legally be excluded or limited under applicable law.",
    ],
  },
  {
    n: "37",
    title: "Customer Complaints",
    body: [
      "37.1 Customers may submit complaints to: Email: info@sharpinhealthcareservices.com",
      "37.2 Complaints should contain sufficient information to enable investigation.",
      "37.3 Sharpin may review complaints concerning: Staff conduct; Attendance; Service quality; Replacement; Billing; Communication; Safety; Other service-related matters.",
      "37.4 Sharpin shall use reasonable efforts to investigate genuine complaints and determine appropriate operational action.",
    ],
  },
  {
    n: "38",
    title: "Intellectual Property",
    body: [
      "38.1 All content appearing on the Sharpin website, including branding, logos, text, graphics, designs, documents and other proprietary materials, is owned by or licensed to Sharpin unless otherwise stated.",
      "38.2 Such material shall not be reproduced, copied, modified, distributed or commercially exploited without appropriate permission.",
    ],
  },
  {
    n: "39",
    title: "Website Use",
    body: [
      "The Customer shall not use the Sharpin website for: Unlawful purposes; Fraudulent activities; Impersonation; Misrepresentation; Unauthorised data collection; Malware or harmful code; Interference with website operations; or Any activity prohibited under applicable law.",
      "Sharpin may restrict access to website features where misuse is reasonably suspected.",
    ],
  },
  {
    n: "40",
    title: "Third-Party Services",
    body: [
      "Sharpin may use third-party providers for certain operational functions, including: Payment processing; Communication; Technology; Hosting; Recruitment; Verification; Logistics; and Other business-support functions.",
      "Such providers may process relevant information only to the extent reasonably necessary for the applicable service or lawful business purpose.",
    ],
  },
  {
    n: "41",
    title: "Force Majeure",
    body: [
      "41.1 Sharpin shall not be considered in breach of these Terms to the extent performance is prevented, delayed or materially affected by a Force Majeure Event.",
      "41.2 Sharpin shall use reasonable efforts to communicate material service disruptions.",
      "41.3 Depending upon circumstances, Sharpin may: Suspend services; Reschedule services; Provide alternative arrangements; Deploy replacement personnel where feasible; Modify operational arrangements; or Process an applicable financial adjustment under these Terms and applicable law.",
      "41.4 Force majeure shall not automatically result in a full refund.",
    ],
  },
  {
    n: "42",
    title: "Death, Hospitalisation or Material Change in Patient Condition",
    body: [
      "42.1 The Customer or authorised family representative should promptly inform Sharpin of any material change affecting the service requirement.",
      "42.2 In the event of death, hospitalisation, transfer, substantial medical deterioration or other material change, Sharpin shall assess the service arrangement and applicable financial consequences.",
      "42.3 Any refund or adjustment shall be governed by: The applicable package; Services consumed; Remaining services; Applicable slab; Costs incurred; The circumstances of the case; and Applicable law.",
      "42.4 Nothing in this section shall be interpreted as excluding a mandatory statutory right.",
    ],
  },
  {
    n: "43",
    title: "Termination by Sharpin",
    body: [
      "Sharpin may suspend or terminate services where reasonably necessary due to: Non-payment; Fraudulent information; Abuse or harassment of staff; Threats or violence; Unsafe working conditions; Illegal activities; Material breach of the agreement; Repeated violation of service conditions; Misuse of personnel; or Other circumstances making continued service commercially, operationally or legally unreasonable.",
      "Where reasonably practicable, Sharpin shall provide notice or an explanation before termination.",
    ],
  },
  {
    n: "44",
    title: "Termination by Customer",
    body: [
      "44.1 A Customer may communicate a request to discontinue services by emailing: info@sharpinhealthcareservices.com",
      "44.2 Such request shall be handled according to the applicable package and refund provisions.",
      "44.3 Except for applicable exceptions, statutory rights, written settlement or other circumstances expressly recognised under these Terms, prepaid packages remain subject to the Zero-Cancellation Policy.",
    ],
  },
  {
    n: "45",
    title: "Dispute Resolution",
    body: [
      "45.1 The parties shall initially attempt to resolve disputes through good-faith discussion.",
      "45.2 The Customer may submit a written complaint to Sharpin before pursuing further remedies, where appropriate.",
      "45.3 Nothing in this clause prevents a Customer from approaching a competent statutory authority or forum where such right is available under applicable law.",
      "45.4 Any dispute shall be governed by the laws applicable in India.",
      "45.5 Subject to mandatory jurisdictional requirements under applicable law, disputes may be brought before the competent courts/authorities having jurisdiction over the applicable matter.",
    ],
  },
  {
    n: "46",
    title: "Consumer Rights",
    body: [
      "Nothing contained in these Terms shall be construed to waive, restrict, remove or contractually exclude any right or remedy that is mandatory and legally available to a Customer under applicable Indian law.",
      "Where any provision of these Terms is found to be inconsistent with a mandatory legal requirement, that provision shall be interpreted or modified only to the extent necessary to comply with such requirement, while the remaining provisions shall continue to operate to the extent legally permissible.",
    ],
  },
  {
    n: "47",
    title: "Severability",
    body: [
      "If any provision of these Terms is declared invalid, unlawful or unenforceable by a competent authority, the remaining provisions shall continue in full force to the extent permitted by law.",
    ],
  },
  {
    n: "48",
    title: "Entire Agreement",
    body: [
      "These Terms, together with the applicable quotation, invoice, service agreement, package confirmation and written communications expressly incorporated into the arrangement, constitute the understanding between Sharpin and the Customer concerning the relevant services.",
      "Where there is a conflict, the specific signed service agreement or written commercial confirmation may prevail over general website terms to the extent expressly stated.",
    ],
  },
  {
    n: "49",
    title: "Amendment of Terms",
    body: [
      "Sharpin may update these Terms from time to time to reflect: Changes in services; Operational changes; Legal or regulatory requirements; Pricing structures; Business practices; or Security and privacy requirements.",
      "The updated version shall be published on the Sharpin website with an updated effective date.",
      "Changes shall not retrospectively alter rights already accrued under a separately executed agreement unless permitted by law or expressly agreed by the parties.",
    ],
  },
  {
    n: "50",
    title: "Electronic Acceptance",
    body: [
      "The Customer acknowledges that acceptance may occur electronically through: Website acceptance; Email confirmation; WhatsApp confirmation; Digital signature; Payment confirmation; Payment through an online payment gateway; or Other electronic communication.",
      "Such acceptance may constitute evidence of the Customer's agreement to the applicable terms, subject to applicable law.",
    ],
  },
  {
    n: "51",
    title: "Communication",
    body: [
      "Official service-related communication should be directed to: Sharpin Healthcare Services, Email: info@sharpinhealthcareservices.com, Phone: 9211949414, Website: www.sharpinhealthcareservices.com",
      "Customers are responsible for ensuring that Sharpin has accurate contact information.",
    ],
  },
  {
    n: "52",
    title: "Privacy Policy",
    body: [
      "Sharpin shall maintain a separate Privacy Policy governing the collection, use, storage, sharing and protection of personal and service-related information.",
      "The Privacy Policy shall form part of the overall contractual framework governing use of Sharpin's website and services.",
      "The Privacy Policy may cover information such as: Name; Contact details; Address; Identity information; Payment information; Transaction information; Service requirements; Patient-related information; Documents submitted for verification; Communication records; Website usage information; Device/browser information; IP-related information; Cookies and similar technologies.",
      "The information may be used for service delivery, customer support, verification, payment processing, staffing, communication, legal compliance, security, fraud prevention and legitimate business operations, subject to applicable law.",
    ],
  },
  {
    n: "53",
    title: "Customer Acknowledgement",
    body: [
      "By booking or using Sharpin's services, the Customer acknowledges that:",
      "• Services are generally payable in advance.",
      "• Package pricing may be based upon a committed service duration.",
      "• Longer packages may carry greater discounts.",
      "• Early discontinuation may result in recalculation of the applicable service value.",
      "• Sharpin follows a Zero-Cancellation Policy, subject to expressly stated exceptions and applicable law.",
      "• Slab 5 has a special exceptional refund mechanism under which, where a refund becomes applicable, the maximum commercial refund for remaining services is ordinarily 50% of the pro-rata value of the remaining/unconsumed services, subject to applicable deductions and law.",
      "• A Force Majeure Event does not automatically guarantee a full refund.",
      "• A 48-hour cancellation notice is an operational requirement and does not itself create an unconditional refund entitlement.",
      "• The Customer is responsible for providing accurate service information.",
      "• The Customer shall comply with reasonable safety and conduct requirements.",
      "• Applicable statutory rights remain unaffected.",
    ],
  },
  {
    n: "54",
    title: "Final Commercial Principle",
    body: [
      "SHARPIN HEALTHCARE SERVICES – CANCELLATION & REFUND PRINCIPLE",
      "Advance Payment → Committed Package → Zero Cancellation",
      "Except where a refund is expressly triggered by: Force Majeure; Bona fide legal dispute; Competent authority/court/Consumer Commission order; Mutual written settlement; Mandatory applicable law; or Another exception expressly approved by Sharpin, the Customer shall not ordinarily be entitled to cancel a confirmed prepaid package or demand a refund merely because the Customer no longer wishes to continue the service.",
      "For Slab 5, where an exceptional refund becomes applicable, the refund shall ordinarily be limited to: 50% × Pro-Rata Value of Remaining/Unconsumed Services, subject to applicable deductions, adjustments and law.",
    ],
  },
  {
    n: "55",
    title: "Contact",
    body: [
      "For questions regarding these Terms, cancellation requests, service concerns or refund requests:",
      "SHARPIN HEALTHCARE SERVICES — Website: www.sharpinhealthcareservices.com · Email: info@sharpinhealthcareservices.com · Phone: 9211949414",
      "Effective Date: 07-09-2026 · Last Updated: 07-09-2026",
      "© Sharpin Healthcare Services. All rights reserved.",
    ],
  },
];

const Table = ({ headers, rows, testid }) => (
  <div className="overflow-x-auto my-4" data-testid={testid}>
    <table className="w-full text-sm border border-slate-200 rounded-xl overflow-hidden">
      <thead>
        <tr className="bg-navy text-white">
          {headers.map((h) => (
            <th key={h} className="text-left font-semibold px-4 py-3">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-alabaster"}>
            {row.map((cell, j) => (
              <td key={j} className="px-4 py-3 text-slate-700 border-t border-slate-100">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TermsPage = () => (
  <main className="bg-alabaster text-navy font-body min-h-screen" data-testid="terms-page">
    <header className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="terms-logo-link">
          <img src="/logo.jpeg" alt="Sharpin Healthcare Services" className="h-11 w-11 rounded-full object-cover ring-2 ring-tealbrand/20" />
          <div className="leading-tight">
            <span className="font-display font-bold text-lg tracking-tight text-navy block">SHARPIN</span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-tealbrand font-semibold">Healthcare</span>
          </div>
        </Link>
        <Link
          to="/"
          data-testid="terms-back-home"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-tealbrand transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Website
        </Link>
      </div>
    </header>

    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-16 md:py-20">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="mb-14"
      >
        <p className="text-xs uppercase tracking-[0.3em] font-bold text-tealbrand mb-4 flex items-center gap-2">
          <FileText className="h-4 w-4" /> Legal
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-navy" data-testid="terms-page-title">
          Terms of Use &amp; Service Agreement
        </h1>
        <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm" data-testid="terms-meta-card">
          <div><span className="text-slate-400 font-semibold text-xs uppercase tracking-wider block">Website</span>www.sharpinhealthcareservices.com</div>
          <div><span className="text-slate-400 font-semibold text-xs uppercase tracking-wider block">Email</span>info@sharpinhealthcareservices.com</div>
          <div><span className="text-slate-400 font-semibold text-xs uppercase tracking-wider block">Phone</span>9211949414</div>
          <div><span className="text-slate-400 font-semibold text-xs uppercase tracking-wider block">Applicable Territory</span>India</div>
          <div><span className="text-slate-400 font-semibold text-xs uppercase tracking-wider block">Effective Date</span>07-09-2026</div>
          <div><span className="text-slate-400 font-semibold text-xs uppercase tracking-wider block">Last Updated</span>07-09-2026</div>
        </div>
      </motion.div>

      <div className="space-y-12">
        {SECTIONS.map((section) => (
          <motion.section
            key={section.n}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl border border-slate-200 p-7 md:p-9"
            data-testid={`terms-section-${section.n}`}
          >
            <div className="flex items-baseline gap-4 mb-5">
              <span className="font-display text-lg font-bold text-tealbrand shrink-0">{section.n}.</span>
              <h2 className="font-display text-xl md:text-2xl font-bold text-navy">{section.title}</h2>
            </div>
            <div className="space-y-3.5">
              {section.body.map((para, i) =>
                para === "TABLE:SLAB_PACKAGES" ? (
                  <Table key={i} testid="slab-packages-table" headers={["Slab", "Package Duration", "Indicative Discount", "Continuation / Renewal Payment"]} rows={SLAB_PACKAGES} />
                ) : para === "TABLE:REFUND_TABLE" ? (
                  <Table key={i} testid="refund-table" headers={["Slab", "Base / MRP Reference", "Applicable Discount", "Applicable Rate"]} rows={REFUND_TABLE} />
                ) : (
                  <p key={i} className="text-sm md:text-base text-slate-600 leading-relaxed">{para}</p>
                )
              )}
            </div>
          </motion.section>
        ))}
      </div>
    </div>

    <Footer />
  </main>
);

export default TermsPage;
